import { useState } from 'react'
import { LayoutList, CheckSquare, ChevronRight } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import ActivityTable from '@/components/activities/ActivityTable'
import ValidationQueue from '@/components/activities/ValidationQueue'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export default function ProductionPage() {
  const { canValidate }    = useAuth()
  const [activeTab, setTab] = useState<'activities' | 'validations'>('activities')

  // Count pending validations for badge
  const { data: pendingCount } = useQuery({
    queryKey: ['pending-validation-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('validations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'PENDIENTE')
      return count ?? 0
    },
    staleTime: 30_000,
  })

  const tabs = [
    { key: 'activities',  label: 'Actividades',  icon: LayoutList,  show: true },
    { key: 'validations', label: 'Validaciones',  icon: CheckSquare, show: canValidate, badge: pendingCount },
  ].filter((t) => t.show)

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Sub-navigation */}
      <div className="bg-white border-b border-gray-200 px-6 pt-5 pb-0 shrink-0">
        <div className="flex items-center gap-1">
          {tabs.map((tab) => {
            const Icon    = tab.icon
            const isActive = activeTab === tab.key

            return (
              <button
                key={tab.key}
                onClick={() => setTab(tab.key as 'activities' | 'validations')}
                className={`
                  flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors
                  ${isActive
                    ? 'border-brand-600 text-brand-700 bg-brand-50'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="bg-orange-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-bold leading-none min-w-[18px] text-center">
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'activities' ? (
          <div className="p-6 h-full flex flex-col gap-4">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <span>Producción</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-700 font-medium">Tabla de actividades</span>
            </div>
            <ActivityTable />
          </div>
        ) : (
          <div className="p-6 h-full overflow-y-auto scrollbar-thin">
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
              <span>Producción</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-700 font-medium">Cola de validaciones</span>
            </div>
            <ValidationQueue />
          </div>
        )}
      </div>
    </div>
  )
}
